import { ReadonlyVec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { ImageObject } from '../../../data/image/ImageObject';
import { TextureView } from '../../../view/TextureView';
import type { CheckerboardPatternPx } from '../../../view/layers/checkerboardPattern';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformBoolean } from '../../utils/uniforms/UniformBoolean';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';
import { UniformInt } from '../../utils/uniforms/UniformInt';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../utils/uniforms/UniformVec3';
import { Renderer } from '../Renderer';
import {
    checkerboardImageFragmentShader,
    checkerboardImageVertexShader,
    defaultImageFragmentShader,
    defaultImageVertexShader,
    petFusionImageFragmentShader,
    petFusionImageVertexShader,
} from './glsl';
import { Modality } from '~/domain/dicom/modality';

export type ImageRenderingOptions = {
    alpha: number;
    /**
     * Where the checkerboard's tiles meet and how big they are, in
     * `gl_FragCoord` pixels of the view being drawn. Set only in
     * {@link ImageViewMode.Checkerboard}; the view computes it from the
     * crosshair and its zoom, so the pattern follows the crosshair and
     * covers the same anatomy at every zoom.
     */
    checkerboard?: CheckerboardPatternPx;
    viewMode: ImageViewMode;
    colorMask: ReadonlyVec3;
    secondaryColorMask: ReadonlyVec3;
    ifPetImageFused?: boolean;
    petFusionColorMapTypeIdx?: number;
};

export enum ImageViewMode {
    Checkerboard = 'checkerboard',
    AlphaBlending = 'alpha-blending',
}

/**
 * Exponent applied to the PET overlay opacity in CT/PET fusion.
 */
const PET_FUSION_OPACITY_GAMMA = 0.6;

/** Texture unit the image volume is bound to; see {@link ImageObject.bindTexture}. */
const IMAGE_VOLUME_TEXTURE_UNIT = 0;

/** Texture unit the per-Z arrival mask is bound to. */
const ARRIVAL_MASK_TEXTURE_UNIT = 1;

/**
 * Shown where a slice of a still-filling volume has not arrived yet.
 *
 * A row with no pixels holds the zero its buffer was allocated with, which a
 * normal CT window maps to a mid-grey indistinguishable from soft tissue. A
 * colour no anatomy takes makes "not loaded" read as not loaded.
 */
export const PENDING_VOXEL_COLOR: ReadonlyVec3 = [0.078, 0.129, 0.278];

/**
 * Shown where retrieval has given up on a slice, so it will never hold pixels.
 *
 * Distinct from {@link PENDING_VOXEL_COLOR} because the two call for different
 * responses: one resolves by waiting, the other never does. Blue against red
 * stays separable under red-green colour vision deficiency, where a blue/green
 * or red/green pair would not.
 */
export const UNAVAILABLE_VOXEL_COLOR: ReadonlyVec3 = [0.31, 0.078, 0.094];

/**
 * Renderer that will render {@link ImageObject ImageObjects}.
 */
export class ImageRenderer implements Renderer {
    /**
     * The default shader that will render {@link ImageObject ImageObjects} on {@link PlaneView}
     * without deformations or other fancy stuff.
     */
    private defaultShader: DefaultImageShader;

    private checkerboardShader: CheckerBoardImageShader;

    private petFusionShader: PetFusionImageShader;

    public constructor() {
        this.defaultShader = new DefaultImageShader();
        this.checkerboardShader = new CheckerBoardImageShader();
        this.petFusionShader = new PetFusionImageShader();
    }

    /**
     * Method to delete the {@link ImageRenderer}.\
     * This will deallocate the shaders after which this {@link ImageRenderer} should no longer be used.
     */
    public delete(): void {
        this.defaultShader.delete();
        this.checkerboardShader.delete();
        this.petFusionShader.delete();
    }

    /**
     * Method to render {@link ImageObject ImageObjects}.
     * @throws if {@link view} of unsupported type is provided. Currently only {@link PlaneView PlaneViews} are supported.
     * @param view The {@link PlaneView} whose {@link PlaneView.projectionViewMatrix projectionViewMatrix} is used for projection.
     * @param primaryImage The primary {@link ImageObject} to render.
     * @param secondaryImage Optional secondary {@link ImageObject} to render (e.g., for fusion).
     * @returns True if the images were successfully rendered, false otherwise.
     */
    public render(
        view: TextureView,
        primaryImage: ImageObject | undefined,
        secondaryImage: ImageObject | undefined,
        renderingOptions: ImageRenderingOptions
    ): boolean {
        if (!primaryImage) {
            return false;
        }
        if (view instanceof PlaneView) {
            gl.enable(gl.BLEND);

            const primaryIsPet =
                primaryImage.parent.seriesMetadata.modality === Modality.PT;
            const secondaryIsPet =
                secondaryImage?.parent.seriesMetadata.modality === Modality.PT;

            // CT/PET fusion (two images, exactly one of them PET) composites
            // the PET colormap over the anatomical base with alpha-over
            // blending. The previous MAX blend let the bright CT grayscale
            // win most channels, leaving the heatmap dim. All other fusion
            // combinations keep the order-independent MAX blend below.
            const isPetOverlayFusion =
                !!secondaryImage &&
                primaryIsPet !== secondaryIsPet &&
                renderingOptions.viewMode === ImageViewMode.AlphaBlending;

            if (isPetOverlayFusion) {
                this.renderPetOverlayFusion(
                    view,
                    primaryImage,
                    secondaryImage,
                    primaryIsPet,
                    renderingOptions
                );
                gl.blendEquation(gl.FUNC_ADD);
                gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
                gl.disable(gl.BLEND);
                return true;
            }

            gl.blendEquation(gl.MAX);
            gl.blendFunc(gl.ONE, gl.ONE);

            // Alpha blending and checkerboard are fusion-only visualizations.
            // With only a primary image present they must not apply, or the
            // primary would render half-transparent or in a checker pattern
            // if stale blending state leaked through when the secondary was
            // unloaded. PET-as-primary is a special case: there is nothing
            // to blend with, but the image must still render through the
            // PET colormap shader at full opacity, otherwise the operator
            // sees a featureless grayscale block instead of SUV-coloured
            // intensities.
            const effectiveOptions: ImageRenderingOptions = secondaryImage
                ? renderingOptions
                : {
                      ...renderingOptions,
                      viewMode: ImageViewMode.AlphaBlending,
                      alpha: 1,
                      ifPetImageFused: primaryIsPet,
                  };

            this.renderImage(view, primaryImage, true, effectiveOptions, true);

            if (secondaryImage) {
                this.renderImage(
                    view,
                    secondaryImage,
                    false,
                    effectiveOptions,
                    true
                );
            }

            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.disable(gl.BLEND);
            return true;
        } else {
            throw new Error(
                `ImageRendering on View of type ${view.constructor} currently not supported.`
            );
        }
    }

    /**
     * Renders a CT/PET fusion by compositing the PET colormap *over* the
     * anatomical (non-PET) base with standard alpha-over blending, the way
     * clinical viewers like MIM present SUV heatmaps.
     *
     * The base is drawn first at full brightness as an opaque backdrop, then
     * the PET image is composited on top with a slider-derived opacity that
     * is biased by {@link PET_FUSION_OPACITY_GAMMA} so the heatmap reads
     * stronger at mid-slider without washing the anatomy out at the
     * midpoint.
     *
     * {@link ImageRenderingOptions.alpha renderingOptions.alpha} is always
     * the *primary* image's opacity fraction (consistent with the rest of
     * this renderer), not "the PET fraction", so the endpoints flip with
     * slot order rather than with modality:
     * - PET is primary: slider 0 shows the base (secondary) only, slider 1
     *   shows PET (primary) only.
     * - PET is secondary: slider 0 shows PET (secondary) only, slider 1
     *   shows the base (primary) only.
     *
     * @param view The {@link PlaneView} to render into.
     * @param primaryImage The primary image slot.
     * @param secondaryImage The secondary image slot.
     * @param primaryIsPet Whether the PET image occupies the primary slot.
     * @param renderingOptions The rendering options ({@link ImageRenderingOptions.alpha alpha} is the raw blend slider value, expressed as the primary image's opacity fraction).
     */
    private renderPetOverlayFusion(
        view: PlaneView,
        primaryImage: ImageObject,
        secondaryImage: ImageObject,
        primaryIsPet: boolean,
        renderingOptions: ImageRenderingOptions
    ): void {
        const petImage = primaryIsPet ? primaryImage : secondaryImage;
        const baseImage = primaryIsPet ? secondaryImage : primaryImage;
        const petIsPrimary = primaryIsPet;
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.ONE, gl.ZERO);
        this.renderImage(
            view,
            baseImage,
            !petIsPrimary,
            {
                ...renderingOptions,
                ifPetImageFused: false,
                alpha: petIsPrimary ? 0 : 1,
            },
            false
        );

        const rawPetAlpha = petIsPrimary
            ? renderingOptions.alpha
            : 1 - renderingOptions.alpha;
        const petOpacity = Math.pow(
            Math.min(Math.max(rawPetAlpha, 0), 1),
            PET_FUSION_OPACITY_GAMMA
        );
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        this.renderImage(
            view,
            petImage,
            petIsPrimary,
            {
                ...renderingOptions,
                ifPetImageFused: true,
                alpha: petIsPrimary ? petOpacity : 1 - petOpacity,
            },
            false
        );
    }

    private renderImage(
        view: PlaneView,
        image: ImageObject | undefined,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions,
        useMaxBlend: boolean
    ): void {
        if (!image) {
            return;
        }
        const vao = image.crossSection(view.projectionViewMatrix);
        if (vao) {
            image.bindTexture();
            vao.bind(0, 1);
            switch (renderingOptions.viewMode) {
                case ImageViewMode.Checkerboard:
                    this.renderCheckerboard(
                        image,
                        isPrimary,
                        renderingOptions,
                        vao.indexCount
                    );
                    break;
                case ImageViewMode.AlphaBlending:
                default:
                    const isPetImage =
                        renderingOptions.ifPetImageFused &&
                        image.parent.seriesMetadata.modality === Modality.PT;
                    if (isPetImage) {
                        this.renderPetFusion(
                            image,
                            isPrimary,
                            renderingOptions,
                            vao.indexCount,
                            useMaxBlend
                        );
                    } else {
                        this.renderDefault(
                            image,
                            isPrimary,
                            renderingOptions,
                            vao.indexCount
                        );
                    }
                    break;
            }

            vao.unbind(0, 1);
            vao.delete();
        }
    }
    /**
     * Method to perform the image rendering using the {@link DefaultImageShader}.
     * This method {@link ShaderProgram.start binds} the {@link DefaultImageShader},
     * Loads values to uniform variables,
     * {@link gl.drawElements renders the triangles},
     * and {@link ShaderProgram.stop unbinds} the {@link DefaultImageShader}.
     *
     * @param image The {@link ImageObject} that is being rendered.
     * Used to get the values for uniform variables.
     * @param isPrimary Whether this is the primary image.
     * @param renderingOptions The rendering options.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     */
    private renderDefault(
        image: ImageObject,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions,
        indexCount: number
    ): void {
        this.defaultShader.start();
        this.defaultShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        this.defaultShader.colorMask.loadVec3(
            isPrimary
                ? renderingOptions.colorMask
                : renderingOptions.secondaryColorMask
        );
        this.defaultShader.alpha.loadFloat(
            isPrimary ? renderingOptions.alpha : 1 - renderingOptions.alpha
        );

        // A still-filling volume carries a per-Z mask so rows that hold no
        // pixels can be drawn as absent rather than as the zero they hold. A
        // complete volume has none, and the shader skips the lookup entirely.
        const showsAbsentRows = image.hasArrivalMask;
        this.defaultShader.useArrivalMask.loadBoolean(showsAbsentRows);
        if (showsAbsentRows) {
            this.defaultShader.pendingColor.loadVec3(PENDING_VOXEL_COLOR);
            this.defaultShader.unavailableColor.loadVec3(
                UNAVAILABLE_VOXEL_COLOR
            );
            gl.activeTexture(gl.TEXTURE0 + ARRIVAL_MASK_TEXTURE_UNIT);
            image.bindArrivalMask();
            gl.activeTexture(gl.TEXTURE0 + IMAGE_VOLUME_TEXTURE_UNIT);
        }

        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);

        if (showsAbsentRows) {
            // Leave no texture bound on a unit this renderer does not own for
            // the next draw; the image volume's own unit 0 stays active.
            gl.activeTexture(gl.TEXTURE0 + ARRIVAL_MASK_TEXTURE_UNIT);
            gl.bindTexture(gl.TEXTURE_2D, null);
            gl.activeTexture(gl.TEXTURE0 + IMAGE_VOLUME_TEXTURE_UNIT);
        }

        this.defaultShader.stop();
    }

    /**
     * Method to perform PET fusion rendering with hot colormap.
     * Applies window/level normalization and selectedRange thresholding
     * to display PET data with clinical colormap visualization.
     *
     * @param image The PET {@link ImageObject} that is being rendered.
     * @param renderingOptions The rendering options.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     * @param useMaxBlend Whether this draw is composited with the MAX blend
     * equation. When true, the shader premultiplies opacity into its output
     * color so the blend slider still weights which image dominates under
     * MAX blending; when false (alpha-over blend), it emits straight color
     * with real alpha instead.
     */
    private renderPetFusion(
        image: ImageObject,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions,
        indexCount: number,
        useMaxBlend: boolean
    ): void {
        this.petFusionShader.start();
        this.petFusionShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        // env.alpha holds the *primary* image alpha. The PET fragment
        // takes its own opacity, so it must be env.alpha when PET is
        // the primary slot and the complement when PET is the
        // secondary slot. Both orientations of CT/PET fusion are now
        // supported, so the previous always-invert was wrong for the
        // PET-as-primary case.
        this.petFusionShader.alpha.loadFloat(
            isPrimary ? renderingOptions.alpha : 1 - renderingOptions.alpha
        );

        const colormapTypeIdx = renderingOptions.petFusionColorMapTypeIdx ?? 0;
        this.petFusionShader.colormapType.loadInt(colormapTypeIdx);
        this.petFusionShader.premultiplyAlpha.loadBoolean(useMaxBlend);

        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);
        this.petFusionShader.stop();
    }

    /**
     * Method to perform checkerboard rendering for image comparison.
     *
     * @param image The {@link ImageObject} that is being rendered.
     * @param renderingOptions The rendering options.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     */
    private renderCheckerboard(
        image: ImageObject,
        isPrimary: boolean,
        renderingOptions: ImageRenderingOptions,
        indexCount: number
    ): void {
        this.checkerboardShader.start();
        this.checkerboardShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        this.checkerboardShader.colorMask.loadVec3(
            isPrimary
                ? renderingOptions.colorMask
                : renderingOptions.secondaryColorMask
        );
        // Each tile shows one image fully; the alpha-blend slider has
        // no meaning here. Loading the slider value would make tiles
        // black at the extremes (e.g. slider=0 → primary tiles vanish
        // because the shader does value *= alpha).
        this.checkerboardShader.alpha.loadFloat(1.0);
        this.checkerboardShader.isPrimary.loadInt(isPrimary ? 1 : 0);
        const pattern = renderingOptions.checkerboard;
        if (!pattern) {
            throw new Error(
                'Checkerboard rendering needs a pattern; the view set none.'
            );
        }
        this.checkerboardShader.checkerboardOrigin.loadVec2(pattern.originPx);
        this.checkerboardShader.checkerboardTilePx.loadVec2(pattern.tilePx);
        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);
        this.checkerboardShader.stop();
    }
}

/**
 * {@link ShaderProgram} that will render {@link ImageObject}-{@link PlaneView} intersections.
 * The shader has two inputs:
 * 1. in vec2 position, the intersection vertex coordinates in destination texture space.
 * 2. in vec3 textureCoords, which are the 3D texture coordinates of the image.
 *
 * It also has 3 uniform variables:
 * 1. uniform vec2 windowLevel, which is the {@link ImageObject.windowingLevel windowingLevel} of the {@link ImageObject}.
 * 2. uniform vec3 colorMask, which is the {@link ImageObject.colorMask colorMask} of the {@link ImageObject}.
 * 3. uniform float alpha, which is the {@link ImageObject.alpha alpha} of the {@link ImageObject}.
 */
class DefaultImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = defaultImageVertexShader;
    private static readonly FRAGMENT_CONTENT = defaultImageFragmentShader;

    public readonly windowLevel: UniformVec2;
    public readonly colorMask: UniformVec3;
    public readonly alpha: UniformFloat;
    public readonly useArrivalMask: UniformBoolean;
    public readonly pendingColor: UniformVec3;
    public readonly unavailableColor: UniformVec3;

    constructor() {
        super(
            DefaultImageShader.VERTEX_CONTENT,
            DefaultImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.colorMask = new UniformVec3('colorMask');
        this.alpha = new UniformFloat('alpha');
        this.useArrivalMask = new UniformBoolean('useArrivalMask');
        this.pendingColor = new UniformVec3('pendingColor');
        this.unavailableColor = new UniformVec3('unavailableColor');

        this.storeUniformLocations(
            this.windowLevel,
            this.colorMask,
            this.alpha,
            this.useArrivalMask,
            this.pendingColor,
            this.unavailableColor
        );
    }

    /**
     * The volume and the arrival mask are samplers of different types, so they
     * must be separated onto their own units before the program can validate.
     * Returns a literal: this runs from the base constructor, before the
     * fields above exist.
     */
    protected override samplerUnits(): ReadonlyArray<
        readonly [string, number]
    > {
        return [
            ['sampler', IMAGE_VOLUME_TEXTURE_UNIT],
            ['arrivalMask', ARRIVAL_MASK_TEXTURE_UNIT],
        ];
    }
}

/**
 * {@link ShaderProgram} for checkerboard view mode.
 * Used to compare two images by showing alternating squares from each.
 */
class CheckerBoardImageShader extends ShaderProgram {
    public readonly windowLevel: UniformVec2;
    public readonly colorMask: UniformVec3;
    public readonly alpha: UniformFloat;
    public readonly isPrimary: UniformInt;
    public readonly checkerboardOrigin: UniformVec2;
    public readonly checkerboardTilePx: UniformVec2;

    constructor() {
        super(
            checkerboardImageVertexShader,
            checkerboardImageFragmentShader,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.colorMask = new UniformVec3('colorMask');
        this.alpha = new UniformFloat('alpha');
        this.isPrimary = new UniformInt('isPrimary');
        this.checkerboardOrigin = new UniformVec2('checkerboardOrigin');
        this.checkerboardTilePx = new UniformVec2('checkerboardTilePx');

        this.storeUniformLocations(
            this.windowLevel,
            this.colorMask,
            this.alpha,
            this.isPrimary,
            this.checkerboardOrigin,
            this.checkerboardTilePx
        );
    }
}

/**
 * {@link ShaderProgram} for PET fusion rendering.
 *
 * The shader applies:
 * 1. Window/level normalization for display range
 * 2. PET fusion colormap mapping (e.g. hot colormap: black -> red -> yellow -> white)
 *
 * Uniforms:
 * - windowLevel: vec2 [window, level] for normalization
 * - alpha: float opacity (0-1)
 * - colormapType: int index selecting the PET fusion colormap
 */
class PetFusionImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = petFusionImageVertexShader;
    private static readonly FRAGMENT_CONTENT = petFusionImageFragmentShader;

    public readonly windowLevel: UniformVec2;
    public readonly alpha: UniformFloat;
    public readonly colormapType: UniformInt;
    public readonly premultiplyAlpha: UniformBoolean;

    constructor() {
        super(
            PetFusionImageShader.VERTEX_CONTENT,
            PetFusionImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.alpha = new UniformFloat('alpha');
        this.colormapType = new UniformInt('colormapType');
        this.premultiplyAlpha = new UniformBoolean('premultiplyAlpha');

        this.storeUniformLocations(
            this.windowLevel,
            this.alpha,
            this.colormapType,
            this.premultiplyAlpha
        );
    }
}
